/**
 * settings.js — Player-local UI preferences (audio, accessibility, display).
 *
 * Stored in localStorage so they persist across sessions on this device.
 * Subscribers are notified via a tiny pub/sub so live components can react
 * to changes without a global state library.
 *
 * Schema:
 *   musicVolume     — number 0..1, default 0.3
 *   sfxVolume       — number 0..1, default 0.3
 *   muteAll         — bool, default false
 *   colorblindMode  — 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia'
 *   fontScale       — 'small' | 'normal' | 'large', default 'normal'
 *   reducedMotion   — bool, default false
 *   compactNumbers  — bool, default false (abbreviate gold/XP as 1.2k / 3.4M)
 */

const STORAGE_KEY = 'loria_user_settings_v1';

export const DEFAULT_SETTINGS = {
  musicVolume: 0.3,
  sfxVolume: 0.3,
  muteAll: false,
  colorblindMode: 'none',
  fontScale: 'normal',
  reducedMotion: false,
  compactNumbers: false,
};

let cached = null;
const subscribers = new Set();

function safeParse(json) {
  try {
    const v = JSON.parse(json);
    return v && typeof v === 'object' ? v : {};
  } catch (e) { return {}; }
}

export function getSettings() {
  if (cached) return cached;
  const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
  const stored = raw ? safeParse(raw) : {};
  cached = { ...DEFAULT_SETTINGS, ...stored };
  return cached;
}

export function updateSettings(patch) {
  const next = { ...getSettings(), ...patch };
  cached = next;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  applyDocumentSettings(next);
  subscribers.forEach(fn => { try { fn(next); } catch (e) {} });
  return next;
}

export function subscribeSettings(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/**
 * Effective music volume (respects muteAll). Same idea for sfx.
 */
export function effectiveMusicVolume() {
  const s = getSettings();
  return s.muteAll ? 0 : s.musicVolume;
}
export function effectiveSfxVolume() {
  const s = getSettings();
  return s.muteAll ? 0 : s.sfxVolume;
}

/**
 * Apply settings that map to <html>/<body> attributes/classes so CSS can
 * key off them (font scale, colorblind mode, reduced motion).
 */
export function applyDocumentSettings(s = getSettings()) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-cb-mode', s.colorblindMode || 'none');
  root.setAttribute('data-font-scale', s.fontScale || 'normal');
  root.setAttribute('data-reduced-motion', s.reducedMotion ? '1' : '0');
}

// Apply on module load so the very first paint reflects user preferences.
if (typeof document !== 'undefined') {
  applyDocumentSettings(getSettings());
}
